/*
 * nicose-disenador.js
 * Hito 2 — Previsualizador all-over print sobre molde clínico (Fabric.js v6).
 *
 * Arquitectura de 3 capas:
 *   Layer 0 — canvas.backgroundColor (color del lienzo)
 *   Layer 1 — fabric.Rect cubre el canvas, fill: fabric.Pattern (upload cliente),
 *             clipPath: molde con absolutePositioned:true (recorta a la silueta)
 *   Layer 2 — molde PNG completo, globalCompositeOperation:'multiply' (sombras)
 *
 * La sección Liquid debe exponer:
 *   <section data-nicose-dp data-mold-mujer data-mold-hombre data-canvas-bg ...>
 *     <canvas class="nicose-dp__canvas">
 *     [data-mold="mujer|hombre"] [data-upload] [data-pattern-size] [data-reset]
 */

(function () {
  'use strict';

  function initSection(section) {
    const moldUrls = {
      mujer: section.dataset.moldMujer,
      hombre: section.dataset.moldHombre
    };
    const canvasBg = section.dataset.canvasBg || '#edf3f9';

    const canvasEl = section.querySelector('.nicose-dp__canvas');
    if (!canvasEl) return;
    const stage = canvasEl.parentElement;

    const canvas = new fabric.Canvas(canvasEl, {
      backgroundColor: canvasBg,
      preserveObjectStacking: true,
      selection: false,
      enableRetinaScaling: true
    });

    const slider = section.querySelector('[data-pattern-size]');
    const uploadInput = section.querySelector('[data-upload]');
    const uploadTitleEl = section.querySelector('[data-upload-title]');
    const resetBtn = section.querySelector('[data-reset]');
    const moldButtons = section.querySelectorAll('[data-mold]');
    const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
    const finalizeBtn = section.querySelector('[data-finalize]');
    const finalizeLabel = section.querySelector('[data-finalize-label]');
    const statusEl = section.querySelector('[data-status]');

    const tallaBtns = section.querySelectorAll('[data-talla]');
    const sizeChartModal = section.querySelector('[data-size-chart-modal]');
    const openSizeChartBtn = section.querySelector('[data-open-size-chart]');
    const closeSizeChartBtn = section.querySelector('[data-close-size-chart]');

    const cloudName = section.dataset.cloudinaryCloud || '';
    const uploadPreset = section.dataset.cloudinaryPreset || '';

    function getSelectedVariantId() {
      const active = section.querySelector('[data-talla][aria-pressed="true"]');
      return active ? active.dataset.variantId : '';
    }

    // ----- State -----
    let currentMold = 'mujer';
    let lastSize = 0;
    let userImageEl = null;          // HTMLImageElement del upload
    let sliderValue = 50;
    let patternRect = null;          // Layer 1
    let overlayMold = null;          // Layer 2
    let moldLoadToken = 0;

    // ----- Slider → patrón -----
    // 0 → ~8 tiles por ancho ; 50 → 3 tiles ; 100 → 1 tile
    function tilesFromSlider(v) {
      if (v <= 50) return 8 - (v / 50) * 5;
      return 3 - ((v - 50) / 50) * 2;
    }
    function patternScaleFor(v) {
      if (!userImageEl) return 1;
      const tiles = tilesFromSlider(v);
      const tileWidth = canvas.getWidth() / Math.max(tiles, 1);
      return tileWidth / userImageEl.naturalWidth;
    }
    function buildPattern() {
      if (!userImageEl) return null;
      const s = patternScaleFor(sliderValue);
      return new fabric.Pattern({
        source: userImageEl,
        repeat: 'repeat',
        patternTransform: [s, 0, 0, s, 0, 0]
      });
    }
    function refreshFill() {
      if (!patternRect) return;
      patternRect.set('fill', buildPattern() || canvasBg);
      canvas.requestRenderAll();
    }

    // ----- Layout helpers -----
    function placeCentered(img, factor) {
      const cw = canvas.getWidth();
      const ch = canvas.getHeight();
      const scale = Math.min(cw / img.width, ch / img.height) * factor;
      img.set({
        scaleX: scale,
        scaleY: scale,
        originX: 'center',
        originY: 'center',
        left: cw / 2,
        top: ch / 2,
        selectable: false,
        evented: false
      });
    }

    function loadMold(gender) {
      const url = moldUrls[gender];
      if (!url) return;
      const myToken = ++moldLoadToken;
      // Dos copias del PNG: una como clipPath (silueta), otra como overlay multiply (sombras)
      Promise.all([
        fabric.Image.fromURL(url, { crossOrigin: 'anonymous' }),
        fabric.Image.fromURL(url, { crossOrigin: 'anonymous' })
      ]).then(function (imgs) {
        if (myToken !== moldLoadToken) return;
        const cw = canvas.getWidth();
        const ch = canvas.getHeight();
        const clipMold = imgs[0];
        const overlay = imgs[1];

        placeCentered(clipMold, 0.9);
        placeCentered(overlay, 0.9);
        clipMold.absolutePositioned = true;   // clipPath en coords del canvas
        overlay.set({ globalCompositeOperation: 'multiply' });

        canvas.clear();
        canvas.backgroundColor = canvasBg;

        patternRect = new fabric.Rect({
          left: 0,
          top: 0,
          width: cw,
          height: ch,
          fill: buildPattern() || canvasBg,
          selectable: false,
          evented: false
        });
        patternRect.clipPath = clipMold;

        overlayMold = overlay;

        canvas.add(patternRect);
        canvas.add(overlayMold);
        canvas.requestRenderAll();
      });
    }

    function applyStageSize() {
      const size = stage.offsetWidth;
      if (size <= 0 || size === lastSize) return;
      lastSize = size;
      stage.style.height = size + 'px';
      canvas.setWidth(size);
      canvas.setHeight(size);
      canvas.calcOffset();
      loadMold(currentMold);
    }

    // ----- Events -----
    moldButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const gender = btn.dataset.mold;
        if (gender === currentMold) return;
        currentMold = gender;
        moldButtons.forEach(function (b) {
          b.setAttribute('aria-pressed', b.dataset.mold === gender ? 'true' : 'false');
        });
        loadMold(gender);
      });
    });

    // Tallas: preseleccionar la primera disponible
    const firstAvailableTalla = Array.from(tallaBtns).find(function (b) {
      return !b.disabled;
    });
    if (firstAvailableTalla) {
      firstAvailableTalla.setAttribute('aria-pressed', 'true');
    }

    tallaBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        tallaBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
      });
    });

    if (uploadInput) {
      uploadInput.addEventListener('change', function (e) {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (file.size > MAX_UPLOAD_BYTES) {
          const sizeMb = (file.size / 1024 / 1024).toFixed(1);
          setStatus('Imagen muy pesada (' + sizeMb + ' MB). Máximo 10 MB.', 'error');
          e.target.value = '';
          return;
        }
        const reader = new FileReader();
        reader.onload = function (ev) {
          const img = new Image();
          img.onload = function () {
            userImageEl = img;
            if (slider) slider.disabled = false;
            if (finalizeBtn) finalizeBtn.disabled = false;
            if (uploadTitleEl) uploadTitleEl.textContent = file.name;
            setStatus('', 'idle');
            refreshFill();
          };
          img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
      });
    }

    if (slider) {
      slider.addEventListener('input', function () {
        sliderValue = parseInt(slider.value, 10) || 0;
        refreshFill();
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        userImageEl = null;
        sliderValue = 50;
        if (slider) {
          slider.value = 50;
          slider.disabled = true;
        }
        if (finalizeBtn) finalizeBtn.disabled = true;
        if (uploadTitleEl) uploadTitleEl.textContent = 'Subir estampado';
        setStatus('', 'idle');
        refreshFill();
      });
    }

    // ----- Hito 3 — Finalizar: render → Cloudinary → /cart/add.js -----

    function setStatus(msg, tone) {
      if (!statusEl) return;
      statusEl.textContent = msg || '';
      statusEl.dataset.tone = tone || 'idle';
    }

    function setBtnLoading(isLoading, label) {
      if (!finalizeBtn) return;
      finalizeBtn.dataset.loading = isLoading ? 'true' : 'false';
      finalizeBtn.disabled = isLoading || !userImageEl;
      if (finalizeLabel && label) finalizeLabel.textContent = label;
    }

    function dataURLToBlob(dataURL) {
      const parts = dataURL.split(',');
      const mime = parts[0].match(/:(.*?);/)[1];
      const bin = atob(parts[1]);
      const len = bin.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    }

    function generateDesignId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return 'design-' + window.crypto.randomUUID();
      }
      return 'design-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    }

    function uploadToCloudinary(blob) {
      const url = 'https://api.cloudinary.com/v1_1/' + cloudName + '/image/upload';
      const fd = new FormData();
      fd.append('file', blob, 'nicose-design.png');
      fd.append('upload_preset', uploadPreset);
      return fetch(url, { method: 'POST', body: fd }).then(function (res) {
        if (!res.ok) {
          return res.text().then(function (txt) {
            throw new Error('Cloudinary ' + res.status + ': ' + txt);
          });
        }
        return res.json();
      });
    }

    function addToCart(previewUrl, tilesValue, designId, variantId) {
      const moldLabel = currentMold === 'mujer' ? 'Mujer' : 'Hombre';
      const body = {
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              'Molde': moldLabel,
              'Diseño': 'Mi diseño personalizado',
              '_design_preview_url': previewUrl,
              '_design_scale': String(tilesValue),
              '_design_id': designId
            }
          }
        ]
      };
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (!res.ok) {
          return res.text().then(function (txt) {
            throw new Error('Shopify ' + res.status + ': ' + txt);
          });
        }
        return res.json();
      });
    }

    if (finalizeBtn) {
      finalizeBtn.addEventListener('click', function () {
        if (!userImageEl) return;

        const variantId = getSelectedVariantId();
        if (!variantId) {
          if (tallaBtns.length === 0) {
            console.error('[nicose-dp] Setting "product" sin configurar en Theme Editor.');
            setStatus('Producto no configurado. Avisa al equipo.', 'error');
          } else {
            setStatus('Elige una talla antes de finalizar.', 'error');
          }
          return;
        }
        if (!cloudName || !uploadPreset) {
          console.error('[nicose-dp] Faltan credenciales Cloudinary (cloud name / upload preset).');
          setStatus('Subida de imagen no configurada. Avisa al equipo.', 'error');
          return;
        }

        setBtnLoading(true, 'Subiendo diseño...');
        setStatus('Subiendo tu diseño a la nube...', 'idle');

        const tiles = parseFloat(tilesFromSlider(sliderValue).toFixed(2));
        const designId = generateDesignId();
        const dataURL = canvas.toDataURL({ format: 'png', multiplier: 1 });
        const blob = dataURLToBlob(dataURL);

        uploadToCloudinary(blob)
          .then(function (cloudRes) {
            const previewUrl = cloudRes.secure_url;
            if (!previewUrl) throw new Error('Cloudinary no devolvio secure_url');
            console.log('[nicose-dp] Cloudinary OK', { previewUrl, tiles, designId });
            setStatus('Agregando al carrito...', 'idle');
            setBtnLoading(true, 'Agregando al carrito...');
            return addToCart(previewUrl, tiles, designId, variantId);
          })
          .then(function (cartRes) {
            console.log('[nicose-dp] /cart/add.js OK', cartRes);
            setStatus('Listo, redirigiendo al carrito...', 'success');
            window.location.href = '/cart';
          })
          .catch(function (err) {
            console.error('[nicose-dp] Error en finalizar:', err);
            setStatus('No pudimos completar la subida. Intenta de nuevo.', 'error');
            setBtnLoading(false, 'Finalizar diseño');
          });
      });
    }

    // ----- Modal Guia de Tallas -----
    if (openSizeChartBtn && sizeChartModal) {
      openSizeChartBtn.addEventListener('click', function () {
        if (typeof sizeChartModal.showModal === 'function') {
          sizeChartModal.showModal();
        } else {
          sizeChartModal.setAttribute('open', '');
        }
      });
    }
    if (closeSizeChartBtn && sizeChartModal) {
      closeSizeChartBtn.addEventListener('click', function () {
        sizeChartModal.close();
      });
    }
    if (sizeChartModal) {
      sizeChartModal.addEventListener('click', function (e) {
        if (e.target === sizeChartModal) sizeChartModal.close();
      });
    }

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(applyStageSize).observe(stage);
    } else {
      applyStageSize();
      window.addEventListener('resize', applyStageSize);
      window.addEventListener('load', applyStageSize);
    }
  }

  function bootAll() {
    if (typeof fabric === 'undefined') {
      window.setTimeout(bootAll, 50);
      return;
    }
    document.querySelectorAll('[data-nicose-dp]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
